import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { useMainPageStore } from '@/store/mainPage';
import {
  MAIN_PAGE_TEMPLATE_SLUGS,
  useTemplatesStore,
} from '@/store/templates';
import type { Template } from '@/store/templates';

type MainPageSectionKey = keyof typeof MAIN_PAGE_TEMPLATE_SLUGS;

type MainPageSections = Record<MainPageSectionKey, Template | undefined>;

export function useMainPageTemplates () {
  const mainPageStore = useMainPageStore();
  const {
    content,
    manager,
  } = storeToRefs(mainPageStore);
  const {
    getSellOnline,
    getPartnerLogo,
    getOffer,
    getManager,
  } = mainPageStore;

  const {
    loadTemplate,
    getSlug,
    getTemplateBySlug,
  } = useTemplatesStore();

  function getSectionTemplate (slug: string): Template | undefined {
    return getTemplateBySlug(getSlug(slug));
  }

  const sections = computed<MainPageSections>(() => ({
    mainSection: getSectionTemplate(MAIN_PAGE_TEMPLATE_SLUGS.mainSection),
    offerSection: getSectionTemplate(MAIN_PAGE_TEMPLATE_SLUGS.offerSection),
    partnerSection: getSectionTemplate(MAIN_PAGE_TEMPLATE_SLUGS.partnerSection),
    sellOnlineSection: getSectionTemplate(MAIN_PAGE_TEMPLATE_SLUGS.sellOnlineSection),
    managerSectionTitle: getSectionTemplate(MAIN_PAGE_TEMPLATE_SLUGS.managerSectionTitle),
    managerSection: getSectionTemplate(MAIN_PAGE_TEMPLATE_SLUGS.managerSection),
  }));

  async function fetchMainPageData (): Promise<void> {
    await Promise.all([
      getSellOnline(),
      getPartnerLogo(),
      getOffer(),
      getManager(),
      ...Object.values(MAIN_PAGE_TEMPLATE_SLUGS)
        .map((slug) => loadTemplate({ slug: getSlug(slug) })),
    ]);
  }

  return {
    content,
    manager,
    sections,
    fetchMainPageData,
  };
}
